import React from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import Box from 'core/ui/Box';
import TextFieldRounded from 'core/ui/TextFieldRounded';
import { useTranslation } from 'react-i18next';
import Typography, { fontStyle } from 'core/ui/Typography';
import DatePickerRounded from 'core/ui/DatePickers/DatePickerRounded';
import TextAreaFieldRounded from 'core/ui/TextAreaFieldRounded';
import { formatDateToApi } from 'core/utils/formatters';

export const UsersFieldsSection = ({ section, setValue, watch, errors }) => {
  const { t } = useTranslation();
  const contact = watch(`section-${section?.id}`) || {};

  const changeObjField = (field, value) => {
    contact[field] = value;
    setValue(`section-${section?.id}`, contact);
  };
  // 'emergency_contact', 'dbo', 'address', 'gender', 'referred_by', 'injury_report'
  return (
    <div className="tw-my-2 tw-flex tw-flex-col tw-gap-y-2">
      {section?.kind === 'dbo' && (
        <div>
          <Typography style={fontStyle.label} variant="body2">
            {t('document.section.kind.dbo').toUpperCase()}
          </Typography>
          <Box>
            <Box display="flex" style={{ maxWidth: 200 }} alignItems="center">
              <DatePickerRounded
                placeholder="MM/dd/yyyy"
                stylePaper={{ width: 160 }}
                value={watch(`section-${section?.id}`)}
                onChange={(v) => setValue(`section-${section?.id}`, formatDateToApi(v))}
                error={errors[`section-${section?.id}`]}
                disablePast={false}
                bordered
                required={section?.required}
                format="MM/dd/yyyy"
              />
            </Box>
          </Box>
        </div>
      )}

      {section?.kind === 'emergency_contact' && (
        <div>
          <div className="tw-mb-2">
            <Typography className="tw-text-md tw-font-bold tw-text-gray-700" variant="body2">
              {t('document.section.kind.emergency_contact').toUpperCase()}
            </Typography>
            {errors[`section-${section?.id}`] && (
              <Typography className="tw-text-xs tw-font-semibold tw-text-red-400">
                Invalid emergency contact
              </Typography>
            )}
          </div>
          <Box className="tw-flex tw-flex-col tw-gap-y-2">
            <Box display="flex" flexGrow="1">
              <TextFieldRounded
                classe="ph-bold"
                value={watch(`section-${section?.id}`)?.emergencyName || ''}
                name="emergencyName"
                label={t('profile.label.name').toUpperCase()}
                onChange={(v) => changeObjField('emergencyName', v)}
                placeholder={t('profile.label.name')}
                required={section?.required}
                bordered
              />
            </Box>
            <Box display="flex" flexGrow="1">
              <TextFieldRounded
                classe="ph-bold"
                value={watch(`section-${section?.id}`)?.emergencyEmail || ''}
                name="emergencyEmail"
                label={t('profile.label.email').toUpperCase()}
                onChange={(v) => changeObjField('emergencyEmail', v)}
                placeholder={t('profile.label.email')}
                required={section?.required}
                bordered
              />
            </Box>
            <Box display="flex" flexGrow="1">
              <TextFieldRounded
                classe="ph-bold"
                value={watch(`section-${section?.id}`)?.emergencyPhone || ''}
                name="emergencyPhone"
                label={t('profile.label.phone').toUpperCase()}
                onChange={(v) => changeObjField('emergencyPhone', v)}
                placeholder={t('profile.label.phone')}
                required={section?.required}
                bordered
              />
            </Box>
          </Box>
        </div>
      )}

      {section?.kind === 'address' && (
        <div className="tw-flex tw-flex-col tw-gap-y-2">
          <div className="tw-mb-2">
            <Typography className="tw-text-md tw-font-bold tw-text-gray-700" variant="body2">
              {t('document.section.kind.address').toUpperCase()}
            </Typography>
            {errors[`section-${section?.id}`] && (
              <Typography className="tw-text-xs tw-font-semibold tw-text-red-400">
                Invalid address
              </Typography>
            )}
          </div>

          <Box>
            <TextFieldRounded
              classe="ph-bold"
              value={watch(`section-${section?.id}`)?.description || ''}
              onChange={(v) => changeObjField('description', v)}
              placeholder="e.g. Home, Work"
              label={t('label.title').toUpperCase()}
              bordered
            />
          </Box>
          <Box>
            <TextFieldRounded
              classe="ph-bold"
              value={watch(`section-${section?.id}`)?.street || ''}
              onChange={(v) => changeObjField('street', v)}
              placeholder={t('form.address.label.address')}
              label={t('form.address.label.address').toUpperCase()}
              required={section?.required}
              bordered
            />
          </Box>
          <Box>
            <TextFieldRounded
              classe="ph-bold"
              value={watch(`section-${section?.id}`)?.street1 || ''}
              onChange={(v) => changeObjField('street1', v)}
              placeholder={t('form.address.label.address')}
              label={t('form.address.label.address2').toUpperCase()}
              bordered
            />
          </Box>
          <Box>
            <TextFieldRounded
              classe="ph-bold"
              value={watch(`section-${section?.id}`)?.city || ''}
              onChange={(v) => changeObjField('city', v)}
              placeholder={t('form.address.label.city')}
              label={t('form.address.label.city').toUpperCase()}
              required={section?.required}
              bordered
            />
          </Box>
          <Box>
            <TextFieldRounded
              classe="ph-bold"
              value={watch(`section-${section?.id}`)?.state || ''}
              onChange={(v) => changeObjField('state', v)}
              placeholder={t('form.address.label.state')}
              label={t('form.address.label.state').toUpperCase()}
              required={section?.required}
              bordered
            />
          </Box>
          <Box>
            <TextFieldRounded
              classe="ph-bold"
              value={watch(`section-${section?.id}`)?.zip || ''}
              onChange={(v) => changeObjField('zip', v)}
              placeholder={t('form.address.label.zip')}
              label={t('form.address.label.zip').toUpperCase()}
              required={section?.required}
              bordered
            />
          </Box>
        </div>
      )}

      {section?.kind === 'gender' && (
        <div>
          <Box display="flex" flexWrap="wrap" flexDirection="column">
            <Typography style={fontStyle.label}>{t('profile.gender').toUpperCase()}</Typography>
            {errors[`section-${section?.id}`] && (
              <Typography className="tw-text-xs tw-font-semibold tw-text-red-400">
                Gender is invalid
              </Typography>
            )}
            <Box>
              <RadioGroup
                row
                aria-label="position"
                name="gender"
                value={watch(`section-${section?.id}`) || null}
              >
                <FormControlLabel
                  value="male"
                  onClick={() => setValue(`section-${section?.id}`, 'male')}
                  control={<Radio size="small" color="primary" />}
                  label={t('profile.label.gender.male')}
                />
                <FormControlLabel
                  value="female"
                  onClick={() => setValue('gender', setValue(`section-${section?.id}`, 'female'))}
                  control={<Radio size="small" color="primary" />}
                  label={t('profile.label.gender.female')}
                />
                <FormControlLabel
                  value="n"
                  onClick={() => setValue(`section-${section?.id}`, 'n')}
                  control={<Radio size="small" color="primary" />}
                  label={t('profile.label.gender.do_not_define_me')}
                />
              </RadioGroup>
            </Box>
          </Box>
        </div>
      )}
      {section?.kind === 'referred_by' && (
        <div>
          <Typography style={fontStyle.label} variant="body2" mb={2}>
            {t('document.section.kind.referred_by').toUpperCase()}
          </Typography>
          <Box>
            <TextFieldRounded
              bordered
              value={watch(`section-${section?.id}`)}
              onChange={(v) => setValue(`section-${section?.id}`, v)}
              error={errors[`section-${section?.id}`]}
              required={section?.required}
            />
          </Box>
        </div>
      )}
      {section?.kind === 'injury_report' && (
        <div>
          <Typography style={fontStyle.label} variant="body2" mb={2}>
            {t('document.section.kind.injury_report').toUpperCase()}
          </Typography>
          <Box>
            <TextAreaFieldRounded
              bordered
              value={watch(`section-${section?.id}`)}
              onChange={(v) => setValue(`section-${section?.id}`, v)}
              error={errors[`section-${section?.id}`]}
              rows={20}
              required={section?.required}
            />
          </Box>
        </div>
      )}
    </div>
  );
};
